import React, { memo, ReactElement } from 'react';
import { Icon, Button, FieldGroup } from '@dexma/ui-components';
import i18n from 'asset/translation';
import { StyledEditDashboardHeader } from 'adapter/in/dashboards/pages/EditDashboard/Header/style';
import gtmIds from 'adapter/in/dashboards/config/gtm';
import { LayoutType } from 'domain/entity/layout';

const gtm = gtmIds.dashboard.editHeader;

export type EditHeaderProps = {
    name: string;
    onEditName: () => void;
    editNameEnabled: boolean;
    onExitEditMode: () => void;
    exitEditModeEnabled: boolean;
    onAddWidget: () => void;
    addWidgetEnabled: boolean;
    addWidgetVariant: string;
    onEditLayout: () => void;
    editLayoutEnabled: boolean;
    isEditingLayout: boolean;
    saveLayoutDisabled: boolean;
    saveLayoutLoading: boolean;
    onSaveLayout: () => void;
    selectedLayout: LayoutType;
    onSelectLayout: (layout: LayoutType) => void;
};

export const EditDashboardHeader = (props: EditHeaderProps): ReactElement => {
    const {
        name,
        onEditName,
        editNameEnabled,
        onExitEditMode,
        exitEditModeEnabled,
        onAddWidget,
        addWidgetEnabled,
        addWidgetVariant,
        onEditLayout,
        editLayoutEnabled,
        isEditingLayout,
        saveLayoutDisabled,
        saveLayoutLoading,
        onSaveLayout,
        selectedLayout,
        onSelectLayout,
    } = props;

    return (
        <StyledEditDashboardHeader data-testid='edit-header' isEditingLayout={isEditingLayout}>
            <div className='edit-name-wrapper'>
                <span>{name}</span>
                {editNameEnabled ? (
                    <Icon onClick={onEditName} name='edit' color='gray800' data-testid='edit-dashboard-icon' />
                ) : (
                    <Icon name='edit' color='gray400' data-testid='edit-dashboard-icon-disabled' className='edit-name-disabled' />
                )}
            </div>
            {isEditingLayout ? (
                <FieldGroup
                    data-testid='field-group-layouts'
                    selectedValues={selectedLayout}
                    variant='custom'
                    size='large'
                    className='layout-fieldgroup'
                    values={[
                        {
                            id: LayoutType.DESKTOP,
                            value: LayoutType.DESKTOP,
                            label: LayoutType.DESKTOP.toLowerCase(),
                            tooltip: i18n.t('dashboards.layouts.desktopTooltip'),
                            icon: 'computer',
                        },
                        {
                            id: LayoutType.TABLET,
                            value: LayoutType.TABLET,
                            label: LayoutType.TABLET.toLowerCase(),
                            tooltip: i18n.t('dashboards.layouts.tabletTooltip'),
                            icon: 'tablet_mac',
                        },
                        {
                            id: LayoutType.MOBILE,
                            value: LayoutType.MOBILE,
                            label: LayoutType.MOBILE.toLowerCase(),
                            tooltip: i18n.t('dashboards.layouts.mobileTooltip'),
                            icon: 'phone_android',
                        },
                    ]}
                    onChange={(layoutOption: { id: LayoutType }) => onSelectLayout(layoutOption.id)}
                />
            ) : (
                <></>
            )}
            <div className='button-wrapper'>
                <Button
                    data-testid='button-add-widget'
                    text={i18n.t('dashboards.edit.addWidget')}
                    variant={addWidgetVariant}
                    dataId={gtm.actions.addWidget}
                    onClick={onAddWidget}
                    isDisabled={!addWidgetEnabled}
                />
                {isEditingLayout ? (
                    <Button
                        data-testid={saveLayoutLoading ? 'button-save-layout-loading' : 'button-save-layout'}
                        text={i18n.t('dashboards.edit.saveLayout')}
                        variant={saveLayoutDisabled ? 'secondary' : 'primary'}
                        dataId={gtm.actions.saveLayout}
                        onClick={onSaveLayout}
                        isLoading={saveLayoutLoading}
                        isDisabled={saveLayoutDisabled}
                    />
                ) : (
                    <Button
                        data-testid='button-edit-layout'
                        text={i18n.t('dashboards.edit.editLayout')}
                        variant='secondary'
                        dataId={gtm.actions.editLayout}
                        onClick={onEditLayout}
                        isDisabled={!editLayoutEnabled}
                    />
                )}
                <Button
                    onClick={onExitEditMode}
                    data-testid='button-exit-edit-mode'
                    text={isEditingLayout ? i18n.t('dashboards.edit.exitEditLayout') : i18n.t('dashboards.edit.exitEditMode')}
                    variant='secondary'
                    dataId={gtm.actions.exitEditMode}
                    isDisabled={!exitEditModeEnabled}
                />
            </div>
        </StyledEditDashboardHeader>
    );
};

export default memo(EditDashboardHeader);
